import { useState } from 'react';

import { Plus } from 'lucide-react';

import { Button } from '@nesso/ui/primitives/button';

import type { TreatmentApproach } from '@/entities/session';
import type { InterventionsInput } from '@/entities/session-note';
import { FieldSection } from '@/shared/ui/forms/field-section';

import { NessoAddApproachDialog } from './nesso-interventions/nesso-add-approach-dialog';
import {
    type InterventionGroup,
    NessoApproachItem,
} from './nesso-interventions/nesso-approach-item';

type InterventionsContent = InterventionsInput;
type Item = { group: InterventionGroup; active: boolean };

type Props = {
    content: InterventionsContent;
    onChange: (content: InterventionsContent) => void;
};

export function NessoInterventions({ content, onChange }: Props) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [items, setItems] = useState<Item[]>(() =>
        content.map((group) => ({ group, active: true })),
    );

    const emit = (next: Item[]) => {
        setItems(next);
        onChange(next.filter((i) => i.active).map((i) => i.group));
    };

    const handleToggle = (approach: string, nextActive: boolean) => {
        emit(
            items.map((i) =>
                i.group.treatmentApproach === approach ? { ...i, active: nextActive } : i,
            ),
        );
    };

    const handleUpdate = (approach: string, update: Partial<InterventionGroup>) => {
        emit(
            items.map((i) =>
                i.group.treatmentApproach === approach
                    ? { ...i, group: { ...i.group, ...update } as InterventionGroup }
                    : i,
            ),
        );
    };

    const addApproaches = (toAdd: TreatmentApproach[]) => {
        const additions = toAdd.map(
            (approach) =>
                ({
                    group: {
                        treatmentApproach: approach,
                        interventions: [],
                        goals: [],
                    } as InterventionGroup,
                    active: true,
                }) satisfies Item,
        );
        emit([...items, ...additions]);
    };

    return (
        <FieldSection title="Interventions" className="gap-8">
            <div className="flex flex-col gap-4.5">
                {items.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">
                        No treatment approaches selected yet.
                    </p>
                )}
                {items.map(({ group, active }) => (
                    <NessoApproachItem
                        key={group.treatmentApproach}
                        group={group}
                        active={active}
                        onUpdate={(update) => handleUpdate(group.treatmentApproach, update)}
                        onToggle={(next) => handleToggle(group.treatmentApproach, next)}
                    />
                ))}
            </div>

            <Button
                variant="ghost"
                size="sm"
                className="-ml-2 self-start font-geist"
                onClick={() => setDialogOpen(true)}
            >
                <Plus className="size-4" />
                Add Approach
            </Button>

            <NessoAddApproachDialog
                open={dialogOpen}
                existingApproaches={items.map(
                    ({ group }) => group.treatmentApproach as TreatmentApproach,
                )}
                onOpenChange={setDialogOpen}
                onAdd={addApproaches}
            />
        </FieldSection>
    );
}
